import { fireEvent, getByText, screen } from '@testing-library/react';
import userEvent, {
  PointerEventsCheckLevel,
} from '@testing-library/user-event';
import { createRef } from 'react';
import { describe, expect, it, vi } from 'vitest';

import { render } from '@dynatrace/strato-components-testing/vitest';

import { Button } from './Button.js';

const TestMarkup = () => (
  <Button>
    <Button.Prefix>Prefix</Button.Prefix>
    <Button.Suffix>Suffix</Button.Suffix>
    <Button.Label>Label</Button.Label>
  </Button>
);

const textItems = ['Label', 'Suffix', 'Prefix'];

describe('Button', () => {
  describe('Basic functionality', () => {
    it('should render children', () => {
      const { container } = render(<Button>Sample</Button>);

      expect(getByText(container, 'Sample')).toBeTruthy();
    });

    it('should apply an aria-label', () => {
      render(<Button aria-label="My label">Sample</Button>);
      expect(screen.getByRole('button', { name: 'My label' })).toBeTruthy();
    });

    it('should set data-dtrum on the root level of the component', () => {
      render(
        <>
          <Button data-testid="dtrum" data-dtrum-mask data-dtrum-allow>
            Sample
          </Button>
          <Button data-testid="no-dtrum">Sample</Button>
        </>,
      );

      expect(screen.getByTestId('dtrum')).toHaveAttribute('data-dtrum-mask');
      expect(screen.getByTestId('dtrum')).toHaveAttribute('data-dtrum-allow');
      expect(screen.getByTestId('no-dtrum')).not.toHaveAttribute(
        'data-dtrum-mask',
      );
      expect(screen.getByTestId('no-dtrum')).not.toHaveAttribute(
        'data-dtrum-allow',
      );
    });

    it('should render children in each slot', () => {
      const { getByText } = render(<TestMarkup />);

      for (const textItem of textItems) {
        expect(getByText(textItem)).toBeTruthy();
      }
    });

    it('should not submit a form', async () => {
      const onSubmit = vi.fn((e) => e.preventDefault());
      const { getByRole } = render(
        <form onSubmit={onSubmit}>
          <Button>Submit</Button>
        </form>,
      );
      await userEvent.click(getByRole('button', { name: 'Submit' }));
      expect(onSubmit).not.toHaveBeenCalled();
    });

    it('should not reset a form', async () => {
      const onReset = vi.fn((e) => e.preventDefault());
      const { getByRole } = render(
        <form onReset={onReset}>
          <Button>Reset</Button>
        </form>,
      );
      await userEvent.click(getByRole('button', { name: 'Reset' }));
      expect(onReset).not.toHaveBeenCalled();
    });

    it('should submit a form', () => {
      const onSubmit = vi.fn((e) => e.preventDefault());
      const { getByRole } = render(
        <form onSubmit={onSubmit}>
          <Button type="submit">Submit</Button>
        </form>,
      );

      fireEvent.click(getByRole('button', { name: 'Submit' }));

      expect(onSubmit).toHaveBeenCalled();
    });

    it('should reset a form', async () => {
      const onReset = vi.fn((e) => e.preventDefault());
      const { getByRole } = render(
        <form onReset={onReset}>
          <Button type="reset">Reset</Button>
        </form>,
      );
      fireEvent.click(getByRole('button', { name: 'Reset' }));
      expect(onReset).toHaveBeenCalled();
    });

    it('should aria-disable the button, mark it busy, and show the progress circle when loading is set to true', async () => {
      const onClick = vi.fn(() => null);

      const { getByRole } = render(
        <Button onClick={onClick} loading={true}>
          Sample
        </Button>,
      );
      const button = screen.getByRole('button');
      await userEvent.click(button);

      expect(getByRole('progressbar')).toBeTruthy();
      expect(button).not.toHaveAttribute('disabled');
      expect(button).toHaveAttribute('aria-disabled', 'true');
      expect(button).toHaveAttribute('aria-busy', 'true');
      expect(onClick).not.toHaveBeenCalled();
    });

    it('should not disable the button or show the progress circle when loading is set to false', async () => {
      const onClick = vi.fn(() => null);

      const { queryByRole } = render(
        <Button onClick={onClick} loading={false}>
          Sample
        </Button>,
      );
      const button = screen.getByRole('button');
      await userEvent.click(button);

      expect(queryByRole('progressbar')).toBeFalsy();
      expect(button).not.toHaveAttribute('disabled');
      expect(onClick).toHaveBeenCalled();
    });

    it('should not disable the button or show the progress circle when loading is not set', async () => {
      const onClick = vi.fn(() => null);

      const { queryByRole } = render(<Button onClick={onClick}>Sample</Button>);
      const button = screen.getByRole('button');
      await userEvent.click(button);

      expect(queryByRole('progressbar')).toBeFalsy();
      expect(button).not.toHaveAttribute('disabled');
      expect(onClick).toHaveBeenCalled();
    });

    it('should not trigger the onClick callback when disabled', async () => {
      const onClick = vi.fn(() => null);

      const { queryByRole } = render(
        <Button onClick={onClick} disabled>
          Sample
        </Button>,
      );
      const button = screen.getByRole('button');
      /**
       * We need to skip the pointer events check of testing-library due to
       * 'pointer-events: none' currently being applied as a workaround for the chromium bug
       * https://github.com/whatwg/html/issues/5886
       */
      await userEvent.click(button, {
        pointerEventsCheck: PointerEventsCheckLevel.Never,
      });

      expect(queryByRole('progressbar')).toBeFalsy();
      expect(button).toHaveAttribute('disabled');
      expect(onClick).not.toHaveBeenCalled();
    });

    it("should not trigger the focus handler when disabled and not show the loading icon if it's not loading", async () => {
      const onFocus = vi.fn(() => null);

      const { queryByRole } = render(
        <Button onFocus={onFocus} disabled>
          Sample
        </Button>,
      );
      await userEvent.tab();
      expect(queryByRole('progressbar')).toBeFalsy();
      expect(onFocus).not.toHaveBeenCalled();
    });

    it('should the number 0 as child', () => {
      const { container } = render(<Button>0</Button>);

      expect(getByText(container, '0')).toBeTruthy();
    });
  });

  it('should not trigger the onClick callback when loading and the button is disabled', async () => {
    const onClick = vi.fn(() => null);

    const { getByRole } = render(
      <Button onClick={onClick} loading disabled>
        Sample
      </Button>,
    );
    const button = screen.getByRole('button');
    await userEvent.click(button);

    expect(getByRole('progressbar')).toBeTruthy();
    expect(button).toHaveAttribute('disabled');
    expect(onClick).not.toHaveBeenCalled();
  });

  it('should not trigger the onClick callback when loading and the button in not disabled', async () => {
    const onClick = vi.fn(() => null);

    const { getByRole } = render(
      <Button onClick={onClick} loading disabled={false}>
        Sample
      </Button>,
    );

    const button = screen.getByRole('button');
    await userEvent.click(button);

    expect(getByRole('progressbar')).toBeTruthy();
    expect(button).not.toHaveAttribute('disabled');
    expect(button).toHaveAttribute('aria-disabled', 'true');
    expect(onClick).not.toHaveBeenCalled();
  });

  it('should trigger the focus handler when loading (button stays in tab order)', async () => {
    const onFocus = vi.fn(() => null);

    const { getByRole } = render(
      <Button onFocus={onFocus} loading>
        Sample
      </Button>,
    );
    await userEvent.keyboard('{Tab}');

    expect(getByRole('progressbar')).toBeTruthy();
    expect(onFocus).toHaveBeenCalled();
  });

  it('should trigger the focus handler when loading and aria-disabled is explicitly set', async () => {
    const onFocus = vi.fn(() => null);

    const { getByRole } = render(
      <Button onFocus={onFocus} loading aria-disabled>
        Sample
      </Button>,
    );
    await userEvent.keyboard('{Tab}');

    expect(getByRole('progressbar')).toBeTruthy();
    expect(onFocus).toHaveBeenCalled();
  });

  it('should trigger the focus handler when loading and disabled is explicitly false', async () => {
    const onFocus = vi.fn(() => null);

    const { getByRole } = render(
      <Button onFocus={onFocus} loading disabled={false}>
        Sample
      </Button>,
    );
    await userEvent.keyboard('{Tab}');

    expect(getByRole('progressbar')).toBeTruthy();
    expect(onFocus).toHaveBeenCalled();
  });

  describe('Polymorphing', () => {
    it('should render as an anchor with an href', () => {
      render(
        <Button as="a" href="https://www.dynatrace.com">
          Sample
        </Button>,
      );
      const anchor = screen.getByRole('link');
      expect(anchor).toBeTruthy();
      expect(anchor.tagName).toEqual('A');
      expect(anchor.getAttribute('href')).toEqual('https://www.dynatrace.com');
    });

    it('should render as a div', () => {
      render(
        <Button
          aria-label="Polymorphic div"
          as="div"
          variant="accent"
          color="primary"
        >
          {'<div>'} tag instead
        </Button>,
      );

      const div = screen.getByLabelText('Polymorphic div');
      expect(div).toBeTruthy();
      expect(div.textContent).toEqual('<div> tag instead');
      expect(div.tagName).toEqual('DIV');
    });

    it('should render as an input', () => {
      render(
        <Button
          as="input"
          variant="accent"
          color="primary"
          value="Add to favorites"
        />,
      );

      const input = document.getElementsByTagName('input');
      expect(input.length).toEqual(1);
      expect(input[0].value).toEqual('Add to favorites');
    });

    it('should render as a custom React component', () => {
      const Link = ({ children, to, ...remainingProps }) => (
        <a href={to} {...remainingProps}>
          {children}
        </a>
      );

      render(
        <Button as={Link} to="https://www.dynatrace.com">
          Sample
        </Button>,
      );
      const anchor = screen.getByRole('link');
      expect(anchor).toBeTruthy();
      expect(anchor.tagName).toEqual('A');
      expect(anchor.getAttribute('href')).toEqual('https://www.dynatrace.com');
    });

    it('should not trigger click when disabled', () => {
      const onClick = vi.fn(() => null);

      render(
        <Button
          as="a"
          href="https://www.dynatrace.com"
          onClick={onClick}
          disabled
        >
          Sample
        </Button>,
      );
      const button = screen.getByRole('link');
      expect(
        async () => await userEvent.click(button, { pointerEventsCheck: 0 }),
      );
      expect(onClick).not.toHaveBeenCalled();
    });

    /**
     * Currently we use polymorphing to switch out the <button> with other tags.
     * However we are passing attributes to the <button> that may not be valid on other tags and there is no way to check or prevent this currently.
     * When this is solved, the test below should work.
     */
    it.skip('should not apply the type attribute when rendered as anchor', () => {
      render(
        <Button as="a" href="https://www.dynatrace.com">
          Sample
        </Button>,
      );
      const anchor = screen.getByRole('link');
      expect(anchor.getAttribute('type')).toBeNull();
    });
  });

  describe('forwardRef', () => {
    it('should forward the ref to the underlying button component', () => {
      const ref = createRef<HTMLButtonElement>();
      render(<Button ref={ref}>Sample</Button>);
      expect(screen.getByRole('button')).toEqual(ref.current);
    });
  });

  describe('behavior tracking attributes', () => {
    it('should add data-dt-* attributes to the button', () => {
      render(
        <Button
          data-dt-content="tracking"
          data-dt-name="MyButton"
          data-dt-children-name="ButtonChildren"
        >
          Sample
        </Button>,
      );

      const button = screen.getByRole('button');
      expect(button).toHaveAttribute('data-dt-content', 'tracking');
      expect(button).toHaveAttribute('data-dt-name', 'MyButton');
      expect(button).toHaveAttribute('data-dt-children-name', 'ButtonChildren');
    });

    describe('data-dt-component', () => {
      it('should forward the data-dt-component to the underlying button component', () => {
        const componentName = 'ButtonComponent';
        render(<Button data-dt-component={componentName}>Sample</Button>);
        const button = screen.getByRole('button');
        expect(button).toHaveAttribute('data-dt-component', componentName);
      });

      it('should have the default data-dt-component attribute', () => {
        render(<Button>Sample</Button>);
        const button = screen.getByRole('button');
        expect(button).toHaveAttribute('data-dt-component', 'Button');
      });
    });

    it('should add data-dt-* attributes to Button.Label', () => {
      render(
        <Button>
          <Button.Label
            data-testid="label"
            data-dt-name="MyLabel"
            data-dt-features="Feature1"
            data-dt-properties="key=value"
          >
            Sample
          </Button.Label>
        </Button>,
      );

      const label = screen.getByTestId('label');
      expect(label).toHaveAttribute('data-dt-name', 'MyLabel');
      expect(label).toHaveAttribute('data-dt-features', 'Feature1');
      expect(label).toHaveAttribute('data-dt-properties', 'key=value');
    });

    it('should add data-dt-* attributes to Button.Prefix', () => {
      render(
        <Button>
          <Button.Prefix
            data-testid="prefix"
            data-dt-name="MyPrefix"
            data-dt-features="Feature1"
            data-dt-properties="key=value"
          >
            Icon
          </Button.Prefix>
        </Button>,
      );

      const prefix = screen.getByTestId('prefix');
      expect(prefix).toHaveAttribute('data-dt-name', 'MyPrefix');
      expect(prefix).toHaveAttribute('data-dt-features', 'Feature1');
      expect(prefix).toHaveAttribute('data-dt-properties', 'key=value');
    });

    it('should add data-dt-* attributes to Button.Suffix', () => {
      render(
        <Button>
          <Button.Suffix
            data-testid="suffix"
            data-dt-name="MySuffix"
            data-dt-features="Feature1"
            data-dt-properties="key=value"
          >
            Badge
          </Button.Suffix>
        </Button>,
      );

      const suffix = screen.getByTestId('suffix');
      expect(suffix).toHaveAttribute('data-dt-name', 'MySuffix');
      expect(suffix).toHaveAttribute('data-dt-features', 'Feature1');
      expect(suffix).toHaveAttribute('data-dt-properties', 'key=value');
    });
  });

  describe('loading', () => {
    it('should set aria-busy when loading', () => {
      render(<Button loading>Sample</Button>);
      expect(screen.getByRole('button')).toHaveAttribute('aria-busy', 'true');
    });

    it('should not set aria-busy when loading is false', () => {
      render(<Button loading={false}>Sample</Button>);
      expect(screen.getByRole('button')).toHaveAttribute('aria-busy', 'false');
    });

    it('should not set aria-busy when loading is not set', () => {
      render(<Button>Sample</Button>);
      expect(screen.getByRole('button')).toHaveAttribute('aria-busy', 'false');
    });

    it('should not set aria-busy when only disabled', () => {
      render(<Button disabled>Sample</Button>);
      expect(screen.getByRole('button')).toHaveAttribute('aria-busy', 'false');
    });
  });

  describe('disabled', () => {
    it('should not trigger onClick callback when disabled', async () => {
      const onClick = vi.fn();

      render(
        <Button disabled onClick={onClick}>
          Sample
        </Button>,
      );
      const button = screen.getByRole('button');
      await userEvent.click(button);

      expect(onClick).not.toHaveBeenCalled();
    });

    it('should not propagate onPointerDown events when disabled', async () => {
      const onPointerDown = vi.fn();

      render(
        <span onPointerDown={onPointerDown}>
          <Button disabled onClick={onPointerDown}>
            Sample
          </Button>
        </span>,
      );
      const button = screen.getByRole('button');
      await userEvent.click(button);

      expect(onPointerDown).not.toHaveBeenCalled();
    });

    it('should not propagate onPointerUp events when disabled', async () => {
      const onPointerUp = vi.fn();

      render(
        <span onPointerUp={onPointerUp}>
          <Button disabled onClick={onPointerUp}>
            Sample
          </Button>
        </span>,
      );
      const button = screen.getByRole('button');
      await userEvent.click(button);

      expect(onPointerUp).not.toHaveBeenCalled();
    });
  });

  describe('className & style', () => {
    it('should add consumer class names and styles', () => {
      render(
        <Button className="test" style={{ backgroundColor: 'khaki' }}>
          <Button.Prefix
            className="test"
            style={{ backgroundColor: 'khaki' }}
            data-testid="button-prefix"
          >
            x
          </Button.Prefix>
          <Button.Label
            className="test"
            style={{ backgroundColor: 'khaki' }}
            data-testid="button-label"
          >
            test
          </Button.Label>
          <Button.Suffix
            className="test"
            style={{ backgroundColor: 'khaki' }}
            data-testid="button-suffix"
          >
            y
          </Button.Suffix>
        </Button>,
      );

      const root = screen.getByRole('button');
      expect(root).toHaveClass('test');
      expect(root).toHaveStyle('background-color: rgb(240, 230, 140)');

      const prefix = screen.getByTestId('button-prefix');
      expect(prefix).toHaveClass('test');
      expect(prefix).toHaveStyle('background-color: rgb(240, 230, 140)');

      const label = screen.getByTestId('button-label');
      expect(label).toHaveClass('test');
      expect(label).toHaveStyle('background-color: rgb(240, 230, 140)');

      const suffix = screen.getByTestId('button-suffix');
      expect(suffix).toHaveClass('test');
      expect(suffix).toHaveStyle('background-color: rgb(240, 230, 140)');
    });
  });
});
